import React, { useState, useRef } from "react";
import { Modal } from "@/components/ui/modal";
import { Image as ImageIcon, Radio, FileText, Sparkles, Wand2, Loader2, X, Check, Mic } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useAura } from "@/context/AuraContext";
import { generateCaption } from "@/lib/ai";
import { api, mediaUrl } from "@/lib/api";
import { AiStudio } from "@/components/ai/AiStudio";
import { MentionInput } from "@/components/common/MentionInput";
import { AudioPlayer } from "@/components/app/AudioPlayer";
import { AudioComposer } from "@/components/app/AudioComposer";
import { VideoEditor } from "@/components/app/VideoEditor";
import { cn } from "@/lib/utils";

const TABS = [
  { id: "post", label: "Post", icon: FileText },
  { id: "media", label: "Photo / Video", icon: ImageIcon },
  { id: "audio", label: "Audio", icon: Mic },
  { id: "live", label: "Go Live", icon: Radio },
];
const MAX_MEDIA = 10;

export function CreateModal({ open, onClose, onPosted }) {
  const [tab, setTab] = useState("post");
  const [text, setText] = useState("");
  const [media, setMedia] = useState([]); // {url, type}
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [studio, setStudio] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editSpec, setEditSpec] = useState(null);
  const [stage, setStage] = useState(""); // "" | processing | publishing
  const fileRef = useRef(null);
  const { user, resendVerification } = useAuth();
  const { spawnBurst } = useAura();
  const verified = !user || user.email_verified || user.allow_post_unverified;
  const audioClip = media.find((m) => m.type === "audio");
  const videoClip = media.find((m) => m.type === "video");

  const reset = () => { setText(""); setMedia([]); setTab("post"); setAi(null); setEditSpec(null); setStage(""); };
  const close = () => { onClose(); setTimeout(reset, 200); };

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    let hasVideo = !!videoClip, count = media.length, addedVideo = false;
    for (const f of files) {
      if (count >= MAX_MEDIA) { toast(`Up to ${MAX_MEDIA} items per post`); break; }
      if (f.type.startsWith("video/") && hasVideo) { toast("One video per post", { description: "You can mix it with up to 9 photos." }); continue; }
      try {
        const r = await api.uploadMedia(f);
        setMedia((m) => [...m, { url: r.url, type: r.type }]);
        count += 1;
        if (r.type === "video") { hasVideo = true; addedVideo = true; setEditSpec(null); }
      } catch (err) { toast("Upload failed", { description: err.message }); }
    }
    setUploading(false);
    if (addedVideo) setEditorOpen(true);
  };

  const askCoach = async () => {
    if (!text.trim()) { toast("Type an idea first"); return; }
    setAiBusy(true);
    try { setAi(await generateCaption(text.trim(), "authentic")); } catch (e) { toast(e.message); } finally { setAiBusy(false); }
  };

  const submit = async () => {
    if (tab === "live") { close(); toast("Live is coming soon"); return; }
    if (!verified) { toast("Confirm your email to post"); return; }
    if (!text.trim() && media.length === 0) { toast("Add text or media first"); return; }
    setPosting(true);
    try {
      let finalMedia = media;
      // Apply video edits server-side before publishing.
      if (videoClip && editSpec) {
        setStage("processing");
        const { _duration, ...spec } = editSpec;
        const edited = await api.processVideo({ url: videoClip.url, ...spec });
        finalMedia = media.map((m) => (m.url === videoClip.url ? { url: edited.url, type: "video", duration: edited.duration, poster: edited.poster } : m));
      }
      setStage("publishing");
      const post = await api.createPost({ text: text.trim(), media: finalMedia });
      spawnBurst(10);
      const st = post.streak;
      if (st && st.first) {
        const mult = ((st.multiplier_bps || 10000) / 10000).toFixed(2) + "x";
        const MILE = { 7: "One week strong", 14: "Two weeks in", 30: "A full month", 50: "50 days — halfway to legend", 100: "100 days. Rare air.", 365: "A full year of Aura" };
        if (MILE[st.streak]) {
          spawnBurst(24);
          toast(`🔥 ${st.streak}-DAY STREAK`, { description: `${MILE[st.streak]} · ${mult} Aura`, duration: 6000 });
        } else {
          toast(`🔥 ${st.streak} day streak`, { description: `Aura multiplier ${mult} · +${Math.round((st.awarded || 0) / 100)} Aura` });
        }
      } else {
        toast("Posted", { description: "+10 Aura" });
      }
      onPosted && onPosted(post);
      close();
    } catch (e) { toast("Couldn't post", { description: e.message }); }
    finally { setPosting(false); setStage(""); }
  };

  return (
    <>
      <Modal open={open} onClose={close} title="Create" testid="create-modal">
        <div className="p-4">
          <div className="mb-4 flex gap-1.5">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`create-tab-${t.id}`}
                className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2.5 text-xs font-semibold transition-colors",
                  tab === t.id ? "border-transparent aura-gradient text-black" : "border-am-border bg-am-surface2 text-am-text2 hover:text-white")}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "live" ? (
            <div className="py-10 text-center">
              <span className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl aura-gradient"><Radio className="h-6 w-6 text-black" /></span>
              <h3 className="font-display text-lg font-bold text-white">Aura Live — Coming Soon</h3>
              <p className="mx-auto mt-1.5 max-w-xs text-sm text-am-text2">Go live, interact with your audience, and build your community in real time.</p>
            </div>
          ) : (
            <>
              <MentionInput as="textarea" placement="bottom" value={text} onChange={(e) => setText(e.target.value)} data-testid="create-caption" rows={3}
                placeholder="What's the move? Share an update… use @ to mention"
                className="resize-none rounded-xl border border-am-border bg-am-surface2 px-4 py-3 text-sm text-white outline-none focus:border-am-border-strong" />

              {audioClip ? (
                <div className="mt-3">
                  <AudioPlayer src={audioClip.url} duration={audioClip.duration} testid="create-audio-preview" />
                  {audioClip.transcript && (
                    <div className="mt-2 rounded-xl border border-am-border bg-am-surface2/60 p-2.5 text-xs text-am-text2" data-testid="create-audio-transcript"><span className="font-semibold text-am-text">Transcript · </span>{audioClip.transcript}</div>
                  )}
                  <button onClick={() => setMedia([])} className="mt-2 text-xs font-semibold text-am-text2 hover:text-white">Remove audio</button>
                </div>
              ) : media.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {media.map((m, i) => (
                    <div key={m.url || i} className="relative h-20 w-20 overflow-hidden rounded-lg border border-am-border">
                      {m.type === "video" ? <video src={mediaUrl(m.url)} className="h-full w-full object-cover" /> : <img src={mediaUrl(m.url)} alt="" className="h-full w-full object-cover" />}
                      <button onClick={() => setMedia((mm) => mm.filter((x) => x.url !== m.url))} className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-black/60 text-white"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}

              {tab === "audio" && !audioClip && <AudioComposer onReady={(m) => setMedia([m])} />}

              {videoClip && (
                <div className="mt-3 flex items-center gap-2" data-testid="video-edit-bar">
                  <button onClick={() => setEditorOpen(true)} data-testid="open-video-editor" className="flex items-center gap-1.5 rounded-full border border-am-border bg-am-surface2 px-3 py-1.5 text-xs font-semibold text-am-text2 hover:text-white">
                    <Wand2 className="h-3.5 w-3.5 aura-text" /> Edit video
                  </button>
                  {editSpec ? (
                    <span className="flex items-center gap-1 rounded-full border border-aura-mint/30 bg-aura-mint/10 px-2.5 py-1 text-xs font-semibold text-aura-mint" data-testid="video-edited-badge"><Check className="h-3 w-3" /> Edited · {Math.round(editSpec._duration || 0)}s</span>
                  ) : (
                    <span className="text-xs text-am-text3">Trim, adjust audio & add text</span>
                  )}
                </div>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {tab !== "audio" && (<>
                <button onClick={() => fileRef.current?.click()} disabled={uploading || media.length >= MAX_MEDIA} data-testid="create-upload" className="flex items-center gap-1.5 rounded-full border border-am-border bg-am-surface2 px-3 py-1.5 text-xs font-semibold text-am-text2 hover:text-white disabled:opacity-60">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                  {uploading ? "Uploading…" : media.length ? `Add more (${media.length}/${MAX_MEDIA})` : "Upload photos / video"}
                </button>
                <input ref={fileRef} type="file" hidden accept="image/*,video/*" multiple onChange={pick} />
                </>)}
                <button onClick={askCoach} disabled={aiBusy} data-testid="create-ai-caption" className="flex items-center gap-1.5 rounded-full p-[1.5px] aura-gradient text-xs font-semibold disabled:opacity-60">
                  <span className="flex items-center gap-1.5 rounded-full bg-am-bg px-3 py-1.5 text-white">{aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 aura-text" />} Aura Coach</span>
                </button>
                <button onClick={() => setStudio(true)} className="flex items-center gap-1.5 rounded-full border border-am-border bg-am-surface2 px-3 py-1.5 text-xs font-semibold text-am-text2 hover:text-white"><Wand2 className="h-3.5 w-3.5 aura-text" /> AI Studio</button>
              </div>

              {ai && (
                <div className="mt-3 space-y-2 rounded-2xl border border-am-border bg-am-surface/50 p-3" data-testid="create-ai-results">
                  {ai.captions?.map((c, i) => <button key={i} onClick={() => setText(c)} data-testid={`ai-caption-${i}`} className="block w-full rounded-lg border border-am-border bg-am-bg/60 px-3 py-2 text-left text-sm text-am-text hover:text-white">{c}</button>)}
                  <div className="flex flex-wrap gap-1.5">{ai.hashtags?.map((h, i) => <button key={i} onClick={() => setText((t) => (t + " " + h).trim())} className="rounded-full border border-aura-cyan/25 bg-am-surface2 px-2.5 py-1 text-xs text-aura-cyan">{h}</button>)}</div>
                </div>
              )}

              {!verified && (
                <div data-testid="create-verify-notice" className="mt-4 flex items-center gap-2 rounded-xl border border-aura-gold/25 bg-aura-gold/[0.06] px-3 py-2.5 text-xs text-am-text2">
                  <span>Confirm your email to publish.</span>
                  <button onClick={() => resendVerification().then(() => toast("Verification email sent")).catch((e) => toast(e.message))} className="font-semibold text-white underline">Resend link</button>
                </div>
              )}

              <button onClick={submit} data-testid="create-submit" disabled={posting || !verified}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-bold text-black transition-transform hover:scale-[1.01] active:scale-95 disabled:cursor-not-allowed disabled:opacity-40">
                {posting ? <><Loader2 className="h-4 w-4 animate-spin" /> {stage === "processing" ? "Processing video…" : "Publishing…"}</> : <><Check className="h-4 w-4" /> Post</>}
              </button>
            </>
          )}
        </div>
      </Modal>
      {videoClip && (
        <VideoEditor open={editorOpen} src={videoClip.url} initial={editSpec}
          onClose={() => setEditorOpen(false)}
          onApply={(spec) => { setEditSpec(spec); setEditorOpen(false); toast("Edits saved", { description: "They'll be applied when you post." }); }} />
      )}
      <AiStudio open={studio} onClose={() => setStudio(false)} kind={videoClip ? "thumbnail" : "post"} title="AI Studio" aspect="square"
        onUse={(img) => setMedia((m) => [...m, { url: img, type: "image" }])} />
    </>
  );
}
